import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  User,
  UserDetailed,
  CreateUserDto,
  UpdateUserProfileDto,
  UpdateUserDto,
  UserQueryParams,
  UserResponse,
  UserDetailedResponse,
  UserListResponse,
  CreateUserResponse
} from '../models';

/**
 * Service for managing users via the User Management API
 * 
 * This service provides comprehensive methods for:
 * - User registration (public endpoint)
 * - User profile management
 * - Admin user operations (CRUD)
 * - User search and listing with pagination
 * 
 * @remarks
 * **Authentication:** All endpoints except registration require authentication via Bearer token.
 * Authentication is handled by HTTP interceptors provided by the consuming application.
 * 
 * **Access Control:**
 * - User registration (POST /api/users) is public
 * - Profile endpoints (GET/PUT /api/users/me) are accessible to authenticated users
 * - Admin endpoints (GET/PUT/DELETE /api/users/:id, GET /api/users) require Admin role
 * 
 * **Organization Linking:**
 * During registration, users can:
 * - Join existing organization via invitation token
 * - Create new organization (becomes Admin automatically)
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private userService: UserService) {}
 * 
 * // Register new user
 * this.userService.createUser({
 *   firebaseUid: 'abc123...',
 *   email: 'user@example.com',
 *   name: 'John Doe',
 *   emailVerified: true
 * }).subscribe(response => {
 *   console.log('User created:', response.data.user);
 * });
 * 
 * // Get current user profile
 * this.userService.getCurrentUser().subscribe(response => {
 *   console.log('Current user:', response.data.user);
 * });
 * 
 * // Update profile
 * this.userService.updateCurrentUserProfile({
 *   bio: 'Software developer',
 *   timezone: 'America/New_York'
 * }).subscribe(response => {
 *   console.log('Profile updated:', response.data.user);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;
  private readonly authUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/users`;
    this.authUrl = `${this.config.apiBaseUrl}/api/auth`;
    this.logDebug('UserService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // PUBLIC ENDPOINTS (No Authentication Required)
  // ============================================================================

  /**
   * Create a new user (Registration)
   * 
   * @param dto - User creation data
   * @returns Observable of API response with created user
   * 
   * @throws {Error} 400 - Invalid request data (validation failed)
   * @throws {Error} 409 - Conflict (email or firebaseUid already exists)
   * 
   * @remarks
   * **Public Endpoint** - No authentication required.
   * 
   * **Request Requirements:**
   * - `firebaseUid` is required (exactly 28 characters)
   * - `email` is required (valid email format, will be lowercased)
   * - `name` is required
   * 
   * **Organization Linking:**
   * - If `invitationToken` provided: Links user to existing organization
   * - If `orgName` and `orgCode` provided: Creates new organization with user as Admin
   * - User is added to organization members automatically
   * 
   * **Response:**
   * Returns the created user object with default values:
   * - `emailVerified`: false
   * - `currency`: 'USD'
   * - `timezone`: 'UTC'
   * - `language`: 'en'
   * - `roles`: ['user']
   * - `isActive`: true
   * - `organizations`: Array of organization IDs user belongs to
   * 
   * @example
   * ```typescript
   * // Register with invitation token
   * this.userService.createUser({
   *   firebaseUid: 'firebase_uid_28_chars_long',
   *   email: 'user@example.com',
   *   name: 'John Doe',
   *   emailVerified: true,
   *   invitationToken: 'invitation-token-here'
   * }).subscribe({
   *   next: response => console.log('User created:', response.data.user),
   *   error: err => console.error('Registration failed:', err)
   * });
   * 
   * // Register and create organization
   * this.userService.createUser({
   *   firebaseUid: 'firebase_uid_28_chars_long',
   *   email: 'admin@example.com',
   *   name: 'Jane Admin',
   *   orgName: 'My Company',
   *   orgCode: 'MYCO'
   * }).subscribe({
   *   next: response => console.log('User and org created:', response.data.user),
   *   error: err => console.error('Registration failed:', err)
   * });
   * ```
   */
  createUser(dto: CreateUserDto): Observable<CreateUserResponse> {
    this.logDebug('Creating user', { email: dto.email });

    return this.http.post<CreateUserResponse>(this.apiUrl, dto).pipe(
      map(response => {
        this.logDebug('User created successfully', { userId: response.data.user._id });
        return response;
      }),
      catchError(error => this.handleError('createUser', error))
    );
  }

  // ============================================================================
  // AUTHENTICATED USER ENDPOINTS (Own Profile)
  // ============================================================================

  /**
   * Get current authenticated user's profile
   * 
   * @returns Observable of API response with user data
   * 
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Bearer token must be provided via interceptor.
   * 
   * Returns the current user's complete profile including:
   * - All user fields except firebaseUid (excluded for security)
   * - Array of organization IDs (use getDetailedCurrentUser for populated data)
   * 
   * @example
   * ```typescript
   * this.userService.getCurrentUser().subscribe({
   *   next: response => {
   *     const user = response.data.user;
   *     console.log(`Welcome ${user.name}!`);
   *     console.log('Organizations:', user.organizations);
   *   },
   *   error: err => console.error('Failed to load profile:', err)
   * });
   * ```
   */
  getCurrentUser(): Observable<UserResponse> {
    this.logDebug('Getting current user profile');

    return this.http.get<UserResponse>(`${this.apiUrl}/me`).pipe(
      map(response => {
        this.logDebug('Current user retrieved', { userId: response.data.user._id });
        return response;
      }),
      catchError(error => this.handleError('getCurrentUser', error))
    );
  }

  /**
   * Get current user's detailed profile with populated organizations
   * 
   * @returns Observable of API response with detailed user data
   * 
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Bearer token must be provided via interceptor.
   * 
   * Returns the current user's profile with populated organization details:
   * - Each organization includes: _id, name, orgCode
   * - Useful for displaying organization names in UI
   * 
   * @example
   * ```typescript
   * this.userService.getDetailedCurrentUser().subscribe({
   *   next: response => {
   *     const user = response.data.user;
   *     console.log('User organizations:');
   *     user.organizations.forEach(org => {
   *       console.log(`- ${org.name} (${org.orgCode})`);
   *     });
   *   },
   *   error: err => console.error('Failed to load detailed profile:', err)
   * });
   * ```
   */
  getDetailedCurrentUser(): Observable<UserDetailedResponse> {
    this.logDebug('Getting detailed current user profile');

    return this.http.get<UserDetailedResponse>(`${this.apiUrl}/profile/detailed`).pipe(
      map(response => {
        this.logDebug('Detailed user retrieved', { 
          userId: response.data.user._id,
          orgCount: response.data.user.organizations.length 
        });
        return response;
      }),
      catchError(error => this.handleError('getDetailedCurrentUser', error))
    );
  }

  /**
   * Update current authenticated user's profile
   * 
   * @param dto - Profile update data (all fields optional)
   * @returns Observable of API response with updated user
   * 
   * @throws {Error} 400 - Invalid request data (validation failed)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Bearer token must be provided via interceptor.
   * Users can only update their own profile.
   * 
   * **Updatable Fields:**
   * - name (max 50 characters)
   * - phoneNumber
   * - bio (max 200 characters)
   * - dateOfBirth (ISO 8601 format)
   * - currency (USD, EUR, GBP, INR, CAD, AUD, JPY, CNY)
   * - timezone
   * - language (en, es, fr, de, it, pt, zh, ja, ko, hi)
   * 
   * **Non-updatable Fields:**
   * - email (immutable)
   * - roles (admin only)
   * - isActive (admin only)
   * 
   * @example
   * ```typescript
   * this.userService.updateCurrentUserProfile({
   *   name: 'John Smith',
   *   bio: 'Software engineer and open source contributor',
   *   timezone: 'America/New_York',
   *   language: 'en',
   *   currency: 'USD'
   * }).subscribe({
   *   next: response => console.log('Profile updated:', response.data.user),
   *   error: err => console.error('Update failed:', err)
   * });
   * ```
   */
  updateCurrentUserProfile(dto: UpdateUserProfileDto): Observable<UserResponse> {
    this.logDebug('Updating current user profile', { updates: Object.keys(dto) });

    return this.http.put<UserResponse>(`${this.apiUrl}/me`, dto).pipe(
      map(response => {
        this.logDebug('Profile updated successfully', { userId: response.data.user._id });
        return response;
      }),
      catchError(error => this.handleError('updateCurrentUserProfile', error))
    );
  }

  // ============================================================================
  // ADMIN ENDPOINTS (Manage All Users)
  // ============================================================================

  /**
   * Get all users with pagination and search (Admin only)
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of API response with user list and pagination
   * 
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (Admin role required)
   * 
   * @remarks
   * **Authentication Required** - Admin role required.
   * 
   * **Query Parameters:**
   * - `page`: Page number (default: 1, min: 1)
   * - `limit`: Items per page (default: 10, min: 1)
   * - `search`: Search query for name or email (case-insensitive partial match)
   * 
   * **Response:**
   * Returns paginated user list with metadata:
   * - `users`: Array of user objects
   * - `pagination`: { page, limit, total, pages }
   * 
   * @example
   * ```typescript
   * // Get first page with default limit
   * this.userService.getAllUsers({ page: 1 }).subscribe({
   *   next: response => {
   *     console.log('Users:', response.data.users);
   *     console.log('Total:', response.data.pagination.total);
   *   },
   *   error: err => console.error('Failed to load users:', err)
   * });
   * 
   * // Search users
   * this.userService.getAllUsers({ 
   *   search: 'john',
   *   page: 1,
   *   limit: 20 
   * }).subscribe({
   *   next: response => console.log('Search results:', response.data.users),
   *   error: err => console.error('Search failed:', err)
   * });
   * ```
   */
  getAllUsers(params?: UserQueryParams): Observable<UserListResponse> {
    this.logDebug('Getting all users', params);

    let httpParams = new HttpParams();
    if (params) {
      if (params.page !== undefined) httpParams = httpParams.set('page', params.page.toString());
      if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
      if (params.search) httpParams = httpParams.set('search', params.search);
    }

    return this.http.get<UserListResponse>(this.apiUrl, { params: httpParams }).pipe(
      map(response => {
        this.logDebug('Users retrieved', { 
          count: response.data.users.length,
          total: response.data.pagination.total 
        });
        return response;
      }),
      catchError(error => this.handleError('getAllUsers', error))
    );
  }

  /**
   * Get user by ID (Admin only)
   * 
   * @param userId - User ID (MongoDB ObjectId)
   * @returns Observable of API response with user data
   * 
   * @throws {Error} 400 - Invalid user ID format
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Admin role required.
   * 
   * Returns complete user profile for the specified user.
   * 
   * @example
   * ```typescript
   * this.userService.getUserById('507f1f77bcf86cd799439011').subscribe({
   *   next: response => console.log('User details:', response.data.user),
   *   error: err => console.error('Failed to load user:', err)
   * });
   * ```
   */
  getUserById(userId: string): Observable<UserResponse> {
    this.logDebug('Getting user by ID', { userId });

    return this.http.get<UserResponse>(`${this.apiUrl}/${userId}`).pipe(
      map(response => {
        this.logDebug('User retrieved', { userId: response.data.user._id });
        return response;
      }),
      catchError(error => this.handleError('getUserById', error))
    );
  }

  /**
   * Update user by ID (Admin only)
   * 
   * @param userId - User ID (MongoDB ObjectId)
   * @param dto - User update data (all fields optional)
   * @returns Observable of API response with updated user
   * 
   * @throws {Error} 400 - Invalid request data (validation failed)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Admin role required.
   * 
   * Admins can update any field including:
   * - All profile fields (name, phoneNumber, bio, dateOfBirth, etc.)
   * - `roles`: Array of role strings (e.g., ['user', 'admin'])
   * - `isActive`: Boolean to enable/disable user account
   * 
   * **Note:** Email and firebaseUid cannot be updated.
   * 
   * @example
   * ```typescript
   * // Update user role and status
   * this.userService.updateUserById('507f1f77bcf86cd799439011', {
   *   roles: ['user', 'admin'],
   *   isActive: true
   * }).subscribe({
   *   next: response => console.log('User updated:', response.data.user),
   *   error: err => console.error('Update failed:', err)
   * });
   * 
   * // Deactivate user
   * this.userService.updateUserById('507f1f77bcf86cd799439011', {
   *   isActive: false
   * }).subscribe({
   *   next: response => console.log('User deactivated'),
   *   error: err => console.error('Deactivation failed:', err)
   * });
   * ```
   */
  updateUserById(userId: string, dto: UpdateUserDto): Observable<UserResponse> {
    this.logDebug('Updating user by ID', { userId, updates: Object.keys(dto) });

    return this.http.put<UserResponse>(`${this.apiUrl}/${userId}`, dto).pipe(
      map(response => {
        this.logDebug('User updated successfully', { userId: response.data.user._id });
        return response;
      }),
      catchError(error => this.handleError('updateUserById', error))
    );
  }

  /**
   * Delete user by ID (Admin only)
   * 
   * @param userId - User ID (MongoDB ObjectId)
   * @returns Observable of void (success indicated by completion)
   * 
   * @throws {Error} 400 - Invalid user ID format
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (Admin role required)
   * @throws {Error} 404 - User not found
   * 
   * @remarks
   * **Authentication Required** - Admin role required.
   * 
   * **Warning:** This permanently deletes the user from the database.
   * Consider using `updateUserById` with `isActive: false` for soft deletion.
   * 
   * @example
   * ```typescript
   * this.userService.deleteUserById('507f1f77bcf86cd799439011').subscribe({
   *   next: () => console.log('User deleted successfully'),
   *   error: err => console.error('Deletion failed:', err)
   * });
   * ```
   */
  deleteUserById(userId: string): Observable<void> {
    this.logDebug('Deleting user by ID', { userId });

    return this.http.delete<void>(`${this.apiUrl}/${userId}`).pipe(
      map(() => {
        this.logDebug('User deleted successfully', { userId });
        return undefined as void;
      }),
      catchError(error => this.handleError('deleteUserById', error))
    );
  }

  // ============================================================================
  // AUTH ENDPOINTS (Public)
  // ============================================================================

  /**
   * Reset the password for an email/password account.
   *
   * @remarks
   * **Public endpoint** — no `Authorization` header required.
   * The backend validates that a `VERIFIED` OTP with `purpose: 'password-reset'`
   * exists for the supplied email (verified within the last 15 minutes) before
   * calling the Firebase Admin SDK to update the credential.
   * All existing Firebase refresh tokens are revoked on success.
   *
   * Rate-limited: 5 requests per 15 minutes per IP.
   *
   * @param email       The user's email address (must match the OTP recipient)
   * @param newPassword The new plain-text password (min 8 chars, must contain
   *                    uppercase, lowercase, digit and special character)
   * @returns Observable of the API success response
   *
   * @throws {Error} 400 - Validation error or OTP window expired
   * @throws {Error} 401 - No valid verified OTP found for the email
   * @throws {Error} 429 - Rate limit exceeded
   *
   * @example
   * ```typescript
   * this.userService.resetPassword('user@example.com', 'NewP@ss1!')
   *   .subscribe({
   *     next: res => console.log(res.message),
   *     error: err => console.error('Reset failed:', err.message)
   *   });
   * ```
   */
  resetPassword(email: string, newPassword: string): Observable<{ status: string; message: string }> {
    this.logDebug('Resetting password', { email });
    return this.http
      .post<{ status: string; message: string }>(`${this.authUrl}/reset-password`, {
        email,
        newPassword,
      })
      .pipe(
        tap(() => this.logDebug('Password reset successfully', { email })),
        catchError(error => this.handleError('resetPassword', error))
      );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Search users by name or email (Admin only)
   * 
   * @param searchTerm - Search query string
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Observable of API response with matching users
   * 
   * @remarks
   * Convenience wrapper around getAllUsers with search parameter.
   * Performs case-insensitive partial matching on name and email fields.
   * 
   * @example
   * ```typescript
   * this.userService.searchUsers('john').subscribe({
   *   next: response => console.log('Found users:', response.data.users),
   *   error: err => console.error('Search failed:', err)
   * });
   * ```
   */
  searchUsers(searchTerm: string, page: number = 1, limit: number = 10): Observable<UserListResponse> {
    return this.getAllUsers({ search: searchTerm, page, limit });
  }

  /**
   * Activate user account (Admin only)
   * 
   * @param userId - User ID to activate
   * @returns Observable of API response with updated user
   * 
   * @remarks
   * Convenience wrapper around updateUserById to set isActive to true.
   * 
   * @example
   * ```typescript
   * this.userService.activateUser('507f1f77bcf86cd799439011').subscribe({
   *   next: response => console.log('User activated:', response.data.user),
   *   error: err => console.error('Activation failed:', err)
   * });
   * ```
   */
  activateUser(userId: string): Observable<UserResponse> {
    return this.updateUserById(userId, { isActive: true });
  }

  /**
   * Deactivate user account (Admin only)
   * 
   * @param userId - User ID to deactivate
   * @returns Observable of API response with updated user
   * 
   * @remarks
   * Convenience wrapper around updateUserById to set isActive to false.
   * Deactivated users cannot log in but data is preserved.
   * 
   * @example
   * ```typescript
   * this.userService.deactivateUser('507f1f77bcf86cd799439011').subscribe({
   *   next: response => console.log('User deactivated:', response.data.user),
   *   error: err => console.error('Deactivation failed:', err)
   * });
   * ```
   */
  deactivateUser(userId: string): Observable<UserResponse> {
    return this.updateUserById(userId, { isActive: false });
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Handle HTTP errors with proper error transformation
   * 
   * @param operation - Name of the operation that failed
   * @param error - HTTP error response
   * @returns Observable that throws formatted error
   */
  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Backend error
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Log debug information if debug mode is enabled
   * 
   * @param message - Debug message
   * @param data - Optional data to log
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[UserService] ${message}`, data || '');
    }
  }
}
